import { useContext, useState, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FastField, Formik, Form } from 'formik';
import dayjs from 'dayjs';
import { Modal } from 'antd';

import { RequestContext } from '../../context/Request.context';
import { createUploadBox } from '../../helpers/form.helper';
import AppConfig from '../../providers/AppConfig';
import config from '../../app.config';
import { BaseButton, BaseDatePicker } from '..';

import './RequestDeletionDetailsModal.scss';

const { maxFileSize } = config;

export const RequestDeletionDetailsModal = (props) => {
  const { t: common } = useTranslation('common');

  const { FILE_UPLOAD_TYPES } = useContext(RequestContext);

  const formRef = useRef(null);
  const uploadInputRef = useRef(null);

  const disabledDeleteRegistrationDate = (date) => date < dayjs();

  const onFormSubmit = () => {
    const values = formRef.current.values;
    props.onConfirm(values);
  };

  const [isFileUploaded, setFileUploadState] = useState(false);
  const [isDeleteRegistrationDateSet, setDeleteRegistrationDate] = useState(null);

  const handleFileUploadFieldChange = (files) => {
    setFileUploadState(!!files.length);
  };

  const handleDeletionDateChange = (date) => {
    setDeleteRegistrationDate(date);
  };

  const isDeleteRegistrationButtonEnabled = isFileUploaded && isDeleteRegistrationDateSet;

  const renderUploadBox = createUploadBox(
    () => {},
    // oxlint-disable-next-line react-hooks/refs
    formRef,
    () => {}
  );

  const renderDatePicker = (props) => <BaseDatePicker full calendarInputField={false} {...props} />;

  return (
    <Modal
      className="search-form"
      closable={true}
      footer={props.footer}
      onCancel={props.onCancel}
      title={props.title}
      open={props.visible}
      width={680}
      styles={{ body: { maxHeight: '60vh', overflow: 'auto' } }}
    >
      <Formik
        innerRef={formRef}
        initialValues={{
          type: FILE_UPLOAD_TYPES.SIGNED_DELETION_REQUEST_FORM,
          files: [],
        }}
        onSubmit={onFormSubmit}
      >
        {() => (
          <Form>
            <div className="request-deletion-details-modal__content">
              <div className="request-deletion-details-modal__text">
                <p className="copy copy--color-dark">
                  <Trans i18nKey="request.requestDeletion.detailsInfo" ns="common">
                    <a
                      href="/public/files/template-deregistration.docx"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link--underlined"
                    />
                    <strong />
                  </Trans>
                </p>
              </div>
              <div className="request-deletion-details-modal__deletion-date">
                <FastField
                  component={renderDatePicker}
                  defaultPickerValue={dayjs().add(1, 'day')}
                  name="deletion_date"
                  label={common('request.requestDeletion.selectDateForDeletion')}
                  disabledDate={disabledDeleteRegistrationDate}
                  format={AppConfig.defaultDateFormat}
                  data-testid="deletion-date"
                  inputReadOnly={true}
                  onChange={handleDeletionDateChange}
                  showToday={false}
                />
              </div>
              <div className="request-deletion-details-modal__upload">
                {renderUploadBox({
                  basic: true,
                  name: 'files',
                  conditions: common('request.form.common.fileDefaultsPDF'),
                  maxFileSize,
                  title: common('request.requestDeletion.uploadYourSignedForm'),
                  type: FILE_UPLOAD_TYPES.SIGNED_DELETION_REQUEST_FORM,
                  config: {
                    multiple: false,
                    shouldUploadManually: true,
                  },
                  uploadInputRef,
                  otherProps: {
                    onChange: handleFileUploadFieldChange,
                  },
                  disabled: isFileUploaded,
                })}
              </div>
              <div className="request-deletion-details-modal__delete-button">
                <BaseButton
                  className="multiline-button"
                  data-testid="details-delete-registration-button"
                  disabled={!isDeleteRegistrationButtonEnabled}
                  type="submit"
                  fill={true}
                >
                  {common('request.requestDeletion.deleteRegistration')}
                </BaseButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default RequestDeletionDetailsModal;
